import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import {
  text,
  paragraph,
} from '@groovehq/internal-design-system/lib/styles/elements'
import { styles as fieldStyles } from '@groovehq/internal-design-system/lib/components/Field/Field.styles'
import toast from '@groovehq/internal-design-system/lib/components/Toast'
import Radio from '@groovehq/internal-design-system/lib/components/Radio/Radio'

import { string, bool } from 'prop-types'
import { selectCurrentAgentById } from 'ducks/agents/selectors'
import { selectCurrentInbox } from 'ducks/billing/selectors/selectCurrentInbox'
import { isVersionSmaller } from 'ducks/billing/util'
import { doUnarchiveAgent } from 'ducks/agents/operations'
import { emptyFunc } from 'util/functions'

import EntityDeleteDrawer from '../EntityDelete'
import TeammatesRemovalLock from '../../TeammatesRemovalLock'
import { AGENT_DESCRIPTIONS } from '../AgentAdd/constants'
import { styles } from './styles'

const UserUnarchive = props => {
  const {
    title,
    open,
    drawerResourceId: agentId,
    onClose,
    onExit,
    onRemove,
  } = props

  const dispatch = useDispatch()
  const [openLockCard, setOpenLockCard] = useState(false)

  const agent = useSelector(state => selectCurrentAgentById(state, agentId))
  const currentInboxPlan = useSelector(selectCurrentInbox)
  const isLiteUserSupported = !isVersionSmaller(
    currentInboxPlan?.pricing?.version,
    'v20'
  )

  const { register, handleSubmit } = useForm({
    defaultValues: {
      role: agent?.role || 'agent',
    },
  })

  const handleOnRemove = useCallback(
    async formData => {
      try {
        await dispatch(
          doUnarchiveAgent(agentId, {
            role: formData.role,
            throwOnError: true,
            toastsEnabled: false,
          })
        )
        if (onRemove) onRemove(agentId)
        toast.notify({
          content: 'User unarchived',
          type: 'positive',
        })
        onExit({ isAfterDelete: true })
      } catch (e) {
        if (e.activity && e.activity[0]?.includes('irregular activity')) {
          setOpenLockCard(true)
        } else {
          toast.notify({
            content: `${app.t('Agent')} action failed`,
            type: 'negative',
          })
          onExit({ isAfterDelete: true })
        }
      }
    },
    [dispatch, agentId, onRemove, onExit]
  )

  const handleCloseRemovalLock = useCallback(
    () => {
      setOpenLockCard(false)
      onExit({ isAfterDelete: true })
    },
    [onExit]
  )

  if (!agent) return null

  const { email } = agent

  return (
    <>
      {!openLockCard && (
        <EntityDeleteDrawer
          {...props}
          onRemove={handleSubmit(handleOnRemove)}
          onClose={emptyFunc}
          onCancel={onClose}
          title={title}
          deleteBtnText={`Unarchive ${app.t('user')}`}
          deletingBtnText={`Unarchiving ${app.t('user')}...`}
          tertiaryBtnText="Cancel"
          open={open}
        >
          <div className="grui mt-7">
            <div css={text.styles.textNormal}>
              <p css={[paragraph.styles.preflight, text.styles.fontMedium]}>
                You are about to unarchive {email}.
              </p>
              <br />
              <p css={paragraph.styles.preflight}>
                Unarchiving this {app.t('user')} will allow {email} to access
                the account again.
              </p>
            </div>
            <div className="grui mt-12">
              <span css={[fieldStyles.labelBox, text.styles.textSm]}>
                Unarchive as:
              </span>
              <Radio
                id="admin"
                {...register('role')}
                className="grui mb-8 block"
                value="admin"
              >
                Admin
                <span css={styles.agentTypeDescription}>
                  {AGENT_DESCRIPTIONS.admin.short}
                </span>
              </Radio>
              <Radio
                id="agent"
                {...register('role')}
                className="grui mb-8 block"
                value="agent"
              >
                Agent
                <span css={styles.agentTypeDescription}>
                  {AGENT_DESCRIPTIONS.agent.short}
                </span>
              </Radio>
              {isLiteUserSupported && (
                <Radio
                  id="viewer"
                  {...register('role')}
                  className="grui mb-8 block"
                  value="viewer"
                >
                  Lite
                  <span css={styles.agentTypeDescription}>
                    {AGENT_DESCRIPTIONS.viewer.short}
                  </span>
                </Radio>
              )}
            </div>
          </div>
        </EntityDeleteDrawer>
      )}
      <TeammatesRemovalLock
        onExit={handleCloseRemovalLock}
        open={openLockCard}
      />
    </>
  )
}

UserUnarchive.propTypes = {
  title: string,
  open: bool.isRequired,
  drawerResourceId: string,
}

UserUnarchive.defaultProps = {
  title: 'Unarchive user',
  drawerResourceId: '',
}

export default UserUnarchive
